import { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Table,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Badge,
  ButtonGroup
} from 'reactstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { getSubScriptions, updateSubScriptions } from '../../../axios/subscription';
import { toast } from 'react-toastify';

const validationSchema = Yup.object({
  name: Yup.string()
    .required('Plan name is required')
    .min(2, 'Plan name must be at least 2 characters'),
  monthlyPrice: Yup.number()
    .required('Monthly price is required')
    .min(0, 'Price must be greater than or equal to 0'),
  yearlyPrice: Yup.number()
    .required('Yearly price is required')
    .min(0, 'Price must be greater than or equal to 0'),
  interval: Yup.string()
    .required('Billing interval is required')
    .oneOf(['monthly', 'yearly'], 'Invalid billing interval'),
  status: Yup.string()
    .required('Status is required')
    .oneOf(['active', 'inactive'], 'Invalid status'),
  checkoutUrl: Yup.string()
});

const SubscriptionPlans = () => {
  const [plans, setPlans] = useState([]);
  const [modal, setModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  // Fetch subscriptions from API
  const fetchPlans = async () => {
    try {
      const response = await getSubScriptions();
      if (response.code === 0 && Array.isArray(response.data)) {
        const mappedPlans = response.data.map((p) => ({
          id: p.id.toString(),
          name: p.name,
          monthlyPrice: parseFloat(p.priceMonthly),
          yearlyPrice: parseFloat(p.priceYearly),
          interval: 'monthly', // default monthly | yearly
          subscribers: 0, // API doesn't give subscribers, placeholder
          status: p.active ? 'active' : 'inactive',
          checkoutUrl: p.squareCheckoutUrl
        }));
        setPlans(mappedPlans);
      }
    } catch (error) {
      console.error('Failed to fetch subscription plans:', error);
    }
  };
  useEffect(() => {
    fetchPlans();
  }, []);

  const toggleModal = () => {
    setModal(!modal);
    if (modal) {
      setEditingPlan(null);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan);
    setModal(true);
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (editingPlan) {
      let payload = {
        id: parseInt(values.id),
        name: values.name,
        squareCheckoutUrl: values.checkoutUrl,
        squareSubscriptionId: '',
        priceMonthly: values.monthlyPrice.toString(),
        priceYearly: values.yearlyPrice.toString()
      };

      let response = await updateSubScriptions(payload);
      if (response.status == 200 || response.status == 201) {
        toast.success(response?.data?.data || '');
        fetchPlans();
      }
    }

    setSubmitting(false);
    toggleModal();
  };

  const getInitialValues = () => {
    if (editingPlan) {
      return {
        id: editingPlan.id,
        name: editingPlan.name,
        monthlyPrice: editingPlan.monthlyPrice,
        yearlyPrice: editingPlan.yearlyPrice,
        interval: editingPlan.interval,
        status: editingPlan.status,
        checkoutUrl: editingPlan.checkoutUrl
      };
    }
    return {
      name: '',
      monthlyPrice: '',
      yearlyPrice: '',
      interval: 'monthly',
      status: 'active',
      checkoutUrl: ''
    };
  };

  const getCurrentPrice = (plan) => {
    return billingPeriod === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
  };

  const getYearlySavings = (plan) => {
    const monthlyTotal = plan.monthlyPrice * 12;
    const yearlySavings = monthlyTotal - plan.yearlyPrice;
    const savingsPercentage = Math.round((yearlySavings / monthlyTotal) * 100);
    return { amount: yearlySavings, percentage: savingsPercentage };
  };

  return (
    <Container fluid>
      <Row>
        <Col>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <div>
                <h3 className="mb-1">Subscription Plans</h3>
                <p className="text-muted mb-0">
                  Manage your subscription plans, pricing, and features.
                </p>
              </div>
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center">
                  <ButtonGroup size="sm">
                    <Button
                      color={billingPeriod === 'monthly' ? 'primary' : 'outline-primary'}
                      onClick={() => setBillingPeriod('monthly')}
                    >
                      Monthly
                    </Button>
                    <Button
                      color={billingPeriod === 'yearly' ? 'primary' : 'outline-primary'}
                      onClick={() => setBillingPeriod('yearly')}
                    >
                      Yearly
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              <Table responsive hover bordered>
                <thead>
                  <tr>
                    <th>Plan Name</th>
                    <th>Price</th>
                    <th>Billing Period</th>
                    <th>Status</th>
                    <th>Checkout</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map((plan) => {
                    const savings = getYearlySavings(plan);
                    return (
                      <tr key={plan.id}>
                        <td>
                          <strong>{plan.name}</strong>
                        </td>
                        <td>
                          <div>
                            <strong>${getCurrentPrice(plan)}</strong>
                            <span className="text-muted">
                              /{billingPeriod === 'monthly' ? 'mo' : 'yr'}
                            </span>
                            {billingPeriod === 'yearly' && savings.percentage > 0 && (
                              <div>
                                <Badge color="success" className="mt-1">
                                  Save {savings.percentage}% (${savings.amount.toFixed(2)})
                                </Badge>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="text-capitalize">{billingPeriod}</td>

                        <td>
                          <Badge color={plan.status === 'active' ? 'success' : 'secondary'} pill>
                            {plan.status}
                          </Badge>
                        </td>
                        <td>
                          {plan.checkoutUrl ? (
                            <a href={plan.checkoutUrl} target="_blank" rel="noreferrer">
                              <Button size="sm" color="info">
                                Checkout
                              </Button>
                            </a>
                          ) : (
                            <Badge color="secondary">N/A</Badge>
                          )}
                        </td>
                        <td className="text-center">
                          <ButtonGroup size="sm">
                            <Button color="outline-primary" onClick={() => handleEditPlan(plan)}>
                              Edit
                            </Button>
                          </ButtonGroup>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Modal for Add/Edit Plan */}
      <Modal isOpen={modal} toggle={toggleModal} size="lg">
        <Formik
          initialValues={getInitialValues()}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              <ModalHeader toggle={toggleModal}>
                {editingPlan ? 'Edit Plan' : 'Add New Plan'}
              </ModalHeader>
              <ModalBody>
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Label for="name">Plan Name</Label>
                      <Input
                        type="text"
                        name="name"
                        id="name"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={!!(errors.name && touched.name)}
                      />
                      <FormFeedback>{errors.name}</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="monthlyPrice">Monthly Price ($)</Label>
                      <Input
                        type="number"
                        name="monthlyPrice"
                        id="monthlyPrice"
                        step="0.01"
                        value={values.monthlyPrice}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={!!(errors.monthlyPrice && touched.monthlyPrice)}
                      />
                      <FormFeedback>{errors.monthlyPrice}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="yearlyPrice">Yearly Price ($)</Label>
                      <Input
                        type="number"
                        name="yearlyPrice"
                        id="yearlyPrice"
                        step="0.01"
                        value={values.yearlyPrice}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={!!(errors.yearlyPrice && touched.yearlyPrice)}
                      />
                      <FormFeedback>{errors.yearlyPrice}</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="interval">Default Billing Interval</Label>
                      <Input
                        type="select"
                        name="interval"
                        id="interval"
                        value={values.interval}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={!!(errors.interval && touched.interval)}
                      >
                        <option value="monthly">Monthly</option>
                        <option value="yearly">Yearly</option>
                      </Input>
                      <FormFeedback>{errors.interval}</FormFeedback>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="planStatus">Status</Label>
                      <Input
                        type="select"
                        name="status"
                        id="planStatus"
                        value={values.status}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        invalid={!!(errors.status && touched.status)}
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                      </Input>
                      <FormFeedback>{errors.status}</FormFeedback>
                    </FormGroup>
                  </Col>
                </Row>
                <FormGroup>
                  <Label for="CheckoutUrl">Checkout Url</Label>
                  <Input
                    type="textarea"
                    name="checkoutUrl"
                    id="checkoutUrl"
                    rows={2}
                    value={values.checkoutUrl}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={!!(errors.checkoutUrl && touched.checkoutUrl)}
                    placeholder="e.g., https://checkout.example.com/basic-plan"
                  />

                  <FormFeedback>{errors.checkoutUrl}</FormFeedback>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button color="secondary" onClick={toggleModal}>
                  Cancel
                </Button>
                <Button color="primary" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving...' : editingPlan ? 'Update Plan' : 'Add Plan'}
                </Button>
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Modal>
    </Container>
  );
};

export default SubscriptionPlans;
